import React, { useEffect, useState, useRef } from "react";
import { Box, CircularProgress, Typography, Button } from "@mui/material";

import PlanSelector from "src/components/pages/PricingPlans/PeriodSelectorSlider";
import SubscriptionPlan from "src/components/pages/PricingPlans/SubscriptionPlan";
import GetPackagesDetail from "src/graphql/query/pricingplan/GetPackagesDetail";
import GET_CURRENT_PAYMENT_METHODS from "src/graphql/query/settings/getCurrentPaymentMethods";
import { Elements } from "@stripe/react-stripe-js";
// import VideoPreviewer from "src/components/shared/atoms/VideoPreviewer";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { loadStripe } from "@stripe/stripe-js";
import FeatureModal from "src/components/pages/PricingPlans/FeaturesModal";
import expressConfig from "src/config/express.json";
import { enqueueSnackbar } from "notistack";

import { useQuery, useLazyQuery } from "@apollo/client";
import AddNewMethodModal from "../Billing/AddNewMethodModal";

import useStyle from "./Style";
import UpgradePlanModal from "./UpgradePlansModal";

interface ModalRefType {
  handleOpen: () => void;
  handleClose: () => void;
}

interface IPrices {
  currency: string;
  interval: string;
  intervalCount: number;
  price: number;
  priceId: string;
}

interface IPlan {
  name: string;
  productId: string;
  description: string;
  prices: IPrices[];
  features: [];
}

const UpgradePlan = () => {
  const { classes } = useStyle();
  const modalRef = useRef<ModalRefType | null>(null);

  const handleClose = () => modalRef.current?.handleClose();
  const handleOpen = () => modalRef.current?.handleOpen();

  const [openFeatureModal, setOpenFeatureModal] = React.useState(false);

  const [plan, setPlan] = useState("annual");
  const [products, setProducts] = useState<IPlan[]>([]);
  const [upgradingPlanData, setUpgradingPlanData] = useState<IPlan>();

  const [openPaymentMethodModal, setOpenPaymentMethodModal] = useState<
    (() => void) | null
  >(null);

  const { stripeId: customerId } = useActiveUserDetail();

  const env = process.env.NODE_ENV || "development";
  const stripePublishableKey = expressConfig[env].STRIPE_PUB_KEY;
  const stripePromise = loadStripe(stripePublishableKey);

  const {
    loading: getPackagesDetailQueryLoading,
    error: getPackagesDetailQueryError,
    data: getPackagesDetailQueryResult,
  } = useQuery(GetPackagesDetail, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (getPackagesDetailQueryResult?.getPackagesDetails?.success) {
      if (getPackagesDetailQueryResult?.getPackagesDetails?.data) {
        // const order = ["Basic", "Standard", "Business"];
        // const orderedResult =
        //   getPackagesDetailQueryResult.getPackagesDetails.data.sort(
        //     (a: IPlan, b: IPlan) =>
        //       order.indexOf(a.name) - order.indexOf(b.name),
        //   );
        // setProducts(orderedResult);
        setProducts(getPackagesDetailQueryResult?.getPackagesDetails?.data);
      }
    } else if (getPackagesDetailQueryResult?.getPackagesDetails?.error) {
      enqueueSnackbar(getPackagesDetailQueryResult.getPackagesDetails.error, {
        variant: "error",
      });
    }
  }, [getPackagesDetailQueryResult]);
  useEffect(() => {
    if (getPackagesDetailQueryError) {
      enqueueSnackbar(getPackagesDetailQueryError.message, {
        variant: "error",
      });
    }
  }, [getPackagesDetailQueryError]);

  const [
    getCurrentPaymentMethods,
    {
      // loading: getCurrentPaymentMethodsLoading,
      error: getCurrentPaymentMethodsError,
      data: getCurrentPaymentMethodsResult,
    },
  ] = useLazyQuery(GET_CURRENT_PAYMENT_METHODS, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (
      getCurrentPaymentMethodsResult &&
      getCurrentPaymentMethodsResult.getCurrentPaymentMethods
    ) {
      if (getCurrentPaymentMethodsResult.getCurrentPaymentMethods) {
        const { success, result } =
          getCurrentPaymentMethodsResult.getCurrentPaymentMethods;
        if (
          success &&
          result.defaultPaymentMethodId &&
          result.paymentMethodsList.length > 0
        ) {
          // open upgrade plan modal
          handleOpen();
        }
        // open add payment modal
        else if (openPaymentMethodModal) {
          openPaymentMethodModal(); // call the stored function
        }
      }
    }
  }, [getCurrentPaymentMethodsResult]);

  useEffect(() => {
    if (getCurrentPaymentMethodsError) {
      enqueueSnackbar(getCurrentPaymentMethodsError.message, {
        variant: "error",
      });
    }
  }, [getCurrentPaymentMethodsError]);

  const handleUpgradePlanChecking = (item: IPlan) => {
    getCurrentPaymentMethods({
      variables: {
        stripeCustomerId: customerId,
      },
    });
    setUpgradingPlanData(item);
  };

  const handleUpgradePlanCheckingFromModal = (
    itemPrices: IPrices[],
    itemName: string,
    itemProduct: string,
    itemDescription: string = "", // default if not available
  ) => {
    getCurrentPaymentMethods({
      variables: {
        stripeCustomerId: customerId,
      },
    });

    const combinedItem: any = {
      prices: itemPrices,
      name: itemName,
      productId: itemProduct,
      description: itemDescription,
    };

    setUpgradingPlanData(combinedItem);
  };

  return (
    <Box className={classes.rootContainer}>
      <Elements stripe={stripePromise}>
        <AddNewMethodModal
          stripeCustomerId={customerId}
          openUpgradePlanModal={handleOpen}
          setOpenPaymentMethodModal={setOpenPaymentMethodModal}
        />
      </Elements>
      <UpgradePlanModal
        plan={plan}
        upgradingPlanData={upgradingPlanData}
        modalRef={modalRef}
        handleClose={handleClose}
      />
      <FeatureModal
        open={openFeatureModal}
        setOpen={setOpenFeatureModal}
        loader={getPackagesDetailQueryLoading}
        products={products}
        handlePackagePurchase={handleUpgradePlanCheckingFromModal}
        plan={plan}
        setPlan={setPlan}
      />
      <Box className={classes.root}>
        <Box className={classes.rootHeaderBox}>
          {/* <Typography className={classes.rootBoxHeading}>
            Upgrade Plan
          </Typography> */}
          <Box className={classes.videoHeader}>
            <Typography className={classes.rootBoxHeading}>
              Upgrade Plan
            </Typography>

            {/* <VideoPreviewer videoId="ELf2MlpUUNY" /> */}
          </Box>
          <Typography className={classes.rootBoxSubHeading}>
            Kuikwit pricing plans are designed to meet your needs as you grow
          </Typography>
        </Box>
        <PlanSelector plan={plan} setPlan={setPlan} setting />
        {getPackagesDetailQueryLoading ? (
          <Box className={classes.circularBox}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box className={classes.planContainer}>
            <Box className={classes.planContentContainer}>
              {products.map((item: IPlan, index: number) => (
                <SubscriptionPlan
                  {...item}
                  keyIndex={index}
                  plan={plan}
                  handleOnClick={() => handleUpgradePlanChecking(item)}
                />
              ))}
            </Box>
            {products.length > 0 && (
              <Button
                className={classes.feature_text}
                onClick={() => setOpenFeatureModal(true)}
              >
                See all features
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UpgradePlan;
